import React, { useEffect, useState, useMemo } from "react";
import {
  TableContainer,
  Grid,
  Box,
  Paper,
  LinearProgress,
  Typography,
  Select,
  MenuItem,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";
import WarningIcon from "@mui/icons-material/Warning";
import LockIcon from "@mui/icons-material/Lock";
import { Header } from "../../../components/global/Header";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import Slider from "@mui/material/Slider";
import useSWR from "swr";
import ProductionStatusDetails from "./ProductionStatusDetails";
import Drawer from "@mui/material/Drawer";
import TableSortLabel from "@mui/material/TableSortLabel";

const EpisodeGrid = () => {
  const [threshold, setThreshold] = useState(0.7);
  const [selectedCategoryItem, setSelectedCategoryItem] = useState(null);
  const [selectedProduction, setSelectedProduction] = useState(null);
  const [selectedColumn, setSelectedColumn] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedItemName, setSelectedItemName] = useState(null);
  const [selectedBudget, setSelectedBudget] = useState(null);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [sortConfig, setSortConfig] = useState({ field: "id", direction: "asc" });

  const { data: productions, error: productionsError } = useSWR("/productions");
  const { data: categoryItems, error: categoryError } = useSWR(
    selectedProduction?.id
      ? `/production-category-item/of-production/${selectedProduction.id}`
      : null
  );

  const { data: itemsDetails, error: detailsError } = useSWR(
    selectedProduction?.id && categoryItems
      ? `/expense/test/${selectedProduction.id}/${categoryItems.map((item) => item.id).join(",")}`
      : null,
    async () => {
      const token = localStorage.getItem("token");

      try {
        const itemRequests = categoryItems.map((item) =>
          fetch(
            `${process.env.REACT_APP_PROTOCOL === 'https' || !process.env.REACT_APP_BACKEND_PORT ? '' : `http://${process.env.REACT_APP_HOST}:${process.env.REACT_APP_BACKEND_PORT}`}/api/expense/test/${selectedProduction.id}/${item.id}`,
            {
              headers: {
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
              },
            }
          )
            .then((res) => {
              if (!res.ok) {
                throw new Error(
                  `Error fetching item ${item.id}: ${res.statusText}`
                );
              }
              return res.json();
            })
            .catch((error) => {
              console.error(`Error fetching item ${item.id}:`, error);
              return null; // Return null for this item if the fetch fails
            })
        );

        const results = await Promise.all(itemRequests);

        // Filter out null responses to avoid processing invalid data
        return results.filter((item) => item !== null);
      } catch (error) {
        console.error("Error fetching items:", error);
        throw error; // Let SWR handle the error
      }
    },
    { refreshInterval: 3700 }
  );

  useEffect(() => {
    const storedThreshold = localStorage.getItem("threshold");
    if (storedThreshold) {
      setThreshold(parseFloat(storedThreshold));
    }
  }, []);

  useEffect(() => {
    const savedProduction = localStorage.getItem("selectedProduction");
    if (savedProduction && productions) {
      const parsedProduction = JSON.parse(savedProduction);
      const production = productions.find((p) => p.id === parsedProduction.id);
      if (production) {
        setSelectedProduction(production);
      }
    }
  }, [productions]);

  // Save selected production to localStorage whenever it changes
  useEffect(() => {
    if (selectedProduction) {
      localStorage.setItem(
        "selectedProduction",
        JSON.stringify(selectedProduction)
      );
    }
  }, [selectedProduction]);

  const handleCellClick = (row, episodeNumber) => {
    const episodeData = row[`episode-${episodeNumber}`];
    if (!episodeData || episodeData.amount === null) {
      return;
    }

    setSelectedItem(row.item_db_id);
    setSelectedColumn(episodeNumber);
    setSelectedItemName(row.item);
    setSelectedBudget(row.budget);
  };

  const handleSort = (field) => {
    setSortConfig({
      field,
      direction: sortConfig.field === field && sortConfig.direction === "asc" ? "desc" : "asc",
    });
  };

  const handlePanelClose = () => {
    setSelectedItem(null);
    setSelectedColumn(null);
    setSelectedItemName(null);
  };

  const handleProductionChange = (event) => {
    setSelectedProduction(productions.find((p) => p.id === event.target.value));
    setThreshold(0.7);
    localStorage.removeItem("threshold");
  };

  const handleThresholdChange = (_, newValue) => {
    setThreshold(newValue);
    localStorage.setItem("threshold", newValue.toString());
  };

  const { rows, columnGroupingModel, sortedRows } = useMemo(() => {
    if (!selectedProduction || !categoryItems || !itemsDetails)
      return { columns: [], rows: [], columnGroupingModel: [], sortedRows: [] };

    const maxEpisodes = selectedProduction.no_of_episodes;

    const episodeColumns = Array.from({ length: maxEpisodes }, (_, i) => ({
      field: `episode-${i + 1}`,
      headerName: `Episode ${i + 1}`,
      width: 120,
      renderCell: (params) => {
        const value = params.value;

        if (value && !isNaN(value)) {
          const formattedAmount = new Intl.NumberFormat("en-IE", {
            style: "currency",
            currency: "EUR",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }).format(parseFloat(value));
          return formattedAmount;
        }
        return "";
      },
    }));

    const gridRows = categoryItems.map((item) => {
      const itemDetailsList = itemsDetails
        .flat()
        .filter((detail) => detail.pci_id === item.id);

      const episodeData = Object.fromEntries(
        Array.from({ length: maxEpisodes }, (_, i) => [
          `episode-${i + 1}`,
          { amount: null, expPred: false, maxEpAm: 0 },
        ])
      );

      let budget = "";

      const categoryItem = categoryItems.find(
        (category) => category.id === item.id
      );

      if (categoryItem) {
        budget = categoryItem.pci_amount;
      }

      itemDetailsList.forEach((itemDetail) => {
        itemDetail.exp_assigned_episodes.forEach((assignedEpisode) => {
          const episodeKey = `episode-${assignedEpisode}`;

          if (itemDetail.exp_pred === true) {
            episodeData[episodeKey].expPred = true;
          }

          const expenses = itemDetail.expense_details.filter(
            (expense) => expense.exp_d_episode === assignedEpisode
          );

          const totalExpense = expenses.reduce(
            (sum, expense) => sum + (expense.exp_d_amount || 0),
            0
          );

          episodeData[episodeKey].amount = (episodeData[episodeKey].amount || 0) + totalExpense;

          if (itemDetail.exp_assigned_episodes?.length === maxEpisodes) {
            episodeData[episodeKey].maxEpAm += totalExpense;
          }
        });
      });

      const totalCost = Object.values(episodeData).reduce(
        (sum, ep) => sum + (ep.amount || 0),
        0
      );
      const validEpisodeCount = Object.values(episodeData).filter(
        (ep) => ep.amount !== null
      ).length;
      const averageCost =
        validEpisodeCount > 0 ? totalCost / validEpisodeCount : 0;

      return {
        item_db_id: item.id,
        id: item.id,
        item: item.pci_descr,
        budget,
        budgetTotal: budget * maxEpisodes,
        average: averageCost,
        totalItemActualCost: totalCost,
        ...Object.fromEntries(
          Object.entries(episodeData).map(([key, value]) => [
            key,
            {
              amount: value.amount,
              exp_pred: value.expPred,
              maxEpAm: value.maxEpAm,
            },
          ])
        ),
      };
    });

    const accumulatedBudget = categoryItems.reduce(
      (sum, item) => sum + (parseFloat(item.pci_amount) || 0),
      0
    );

    const totalItemActualCostAllRows = gridRows.reduce(
      (sum, row) => sum + (parseFloat(row.totalItemActualCost) || 0),
      0
    );

    const columnGroupingModel = [
      {
        groupId: "unamortized",
        headerAlign: "center",
        headerName: "Αναπόσβεστη Αξία",
        description: "",
        children: [
          {
            groupId: "total",
            headerAlign: "center",
            headerName: "Σύνολο",
            children: [{ field: "item" }],
          },
          {
            groupId: "budget",
            headerName: (() => {
              return new Intl.NumberFormat("en-IE", {
                style: "currency",
                currency: "EUR",
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }).format(accumulatedBudget);
            })(),
            description: "Total Budget",
            headerAlign: "right",
            children: [{ field: "budget" }],
          },
          {
            groupId: "average",
            headerName: "",
            description: "",
            headerAlign: "right",
            children: [{ field: "average" }],
          },
        ],
      },
      {
        groupId: `Ep. ${maxEpisodes + 1}`,
        headerAlign: "right",
        headerName: "",
        description: "",
        children: [
          {
            groupId: "budgetTotal",
            headerAlign: "right",
            headerName: new Intl.NumberFormat("en-IE", {
              style: "currency",
              currency: "EUR",
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }).format(accumulatedBudget * maxEpisodes),
            description: "Total Budget",
            children: [{ field: "budgetTotal" }],
          },
        ],
      },
      {
        groupId: `Ep. ${maxEpisodes + 2}`,
        headerAlign: "right",
        headerName: "",
        description: "",
        children: [
          {
            groupId: "totalItemActualCost",
            headerAlign: "right",
            headerName: new Intl.NumberFormat("en-IE", {
              style: "currency",
              currency: "EUR",
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }).format(totalItemActualCostAllRows),
            description: "Total Items",
            children: [{ field: "totalItemActualCost" }],
          },
        ],
      },
      ...Array.from({ length: maxEpisodes }, (_, i) => {
        const episodeNumber = i + 1;
        const episodeSum = gridRows.reduce(
          (sum, row) => sum + parseFloat(row[`episode-${episodeNumber}`]?.amount || 0),
          0
        );

        const totalOfFullAssigned = gridRows.reduce(
          (sum, row) =>
            sum + (parseFloat(row[`episode-${episodeNumber}`]?.maxEpAm) || 0),
          0
        );

        const projectionValue = totalOfFullAssigned * (maxEpisodes - i); //* (maxEpisodes - i)
        const formattedProjection = new Intl.NumberFormat("en-IE", {
          style: "currency",
          currency: "EUR",
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(projectionValue);

        const formattedEpisodeSum = new Intl.NumberFormat("en-IE", {
          style: "currency",
          currency: "EUR",
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(episodeSum);

        const isExceeded = episodeSum > accumulatedBudget;
        const episodeSumStyle = isExceeded
          ? { color: "red" }
          : { color: "#2bc12b" };

        return {
          groupId: `Ep. ${episodeNumber}`,
          headerAlign: "right",
          headerName: formattedProjection,
          description: "",
          children: [
            {
              groupId: `Episode ${episodeNumber}`,
              headerAlign: "right",
              headerName: (
                <span style={episodeSumStyle}>{formattedEpisodeSum}</span>
              ),
              description: ` `,
              children: [{ field: `episode-${episodeNumber}` }],
            },
          ],
        };
      }),
    ];

    // Sort rows
    const sorted = [...gridRows].sort((a, b) => {
      const aVal = a[sortConfig.field];
      const bVal = b[sortConfig.field];
      
      if (sortConfig.field.startsWith('episode-')) {
        const aAmount = aVal?.amount || 0;
        const bAmount = bVal?.amount || 0;
        return sortConfig.direction === "asc" ? aAmount - bAmount : bAmount - aAmount;
      }
      
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortConfig.direction === "asc" 
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }
      
      const aNum = parseFloat(aVal) || 0;
      const bNum = parseFloat(bVal) || 0;
      return sortConfig.direction === "asc" ? aNum - bNum : bNum - aNum;
    });

    return {
      columns: [
        { field: "item", headerName: "Στοιχεία", width: 200 },
        { field: "budget", headerName: "Προϋπ.", width: 150 },
        ...episodeColumns,
      ],
      rows: [...gridRows],
      sortedRows: sorted,
      columnGroupingModel,
    };
  }, [selectedProduction, categoryItems, itemsDetails, sortConfig]);

  const isLoading = selectedProduction && (!categoryItems || !itemsDetails);

  if (productionsError || categoryError || detailsError) {
    return <Typography>Error loading data.</Typography>;
  }

  const getColumnGroupLeaves = (group, callback) => {
    const traverse = (node) => {
      if (node.children) {
        node.children.forEach((child) => {
          if (child.field) {
        callback(child.field);
      } else {
            traverse(child);
          }
        });
      }
    };
    traverse(group);
  };

  const toggleColumnVisibility = (field) => {
    setColumnVisibility((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const toggleColumnGroup = (group, checked) => {
      const fields = [];
      getColumnGroupLeaves(group, (field) => fields.push(field));
    setColumnVisibility((prev) => {
      const newState = { ...prev };
      fields.forEach((field) => {
        newState[field] = !checked;
      });
      return newState;
    });
  };

  const isColumnVisible = (field) => {
    return columnVisibility[field] !== false;
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return null;
    return new Intl.NumberFormat("en-IE", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const renderEpisodeCell = (row, episodeNumber) => {
    const episodeData = row[`episode-${episodeNumber}`];
    if (!episodeData || episodeData.amount === null) return "";

    const { amount, exp_pred } = episodeData;
    const budget = parseFloat(row.budget);
    const isAboveBudget = amount > budget;
    const isBelowThreshold = !isAboveBudget && amount < budget * threshold;

    const formattedAmount = formatCurrency(amount);

    return (
      <TableCell
        key={`episode-${episodeNumber}`}
        align="right"
        onClick={() => handleCellClick(row, episodeNumber)}
        sx={{
          cursor: "pointer",
          color: isAboveBudget ? "red" : isBelowThreshold ? "orange" : "inherit",
          fontWeight: isAboveBudget || isBelowThreshold ? "bold" : "inherit",
          position: "relative",
          borderRight: "1px solid #ddd",
          borderBottom: "none",
          padding: "8px 8px",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: "4px",
          }}
        >
          {exp_pred && (
            <div
              style={{
                position: "absolute",
                top: 0,
                right: "98.5px",
                width: 0,
                height: 0,
                borderRight: "10px solid transparent",
                borderTop: "10px solid green",
                zIndex: 1,
              }}
            />
          )}
          {amount !== null && isBelowThreshold && !isAboveBudget && (
            <WarningIcon fontSize="small" sx={{ color: "orange" }} />
          )}
          {formattedAmount}
        </div>
      </TableCell>
    );
  };

  return (
    <Box m="20px">
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Header
          title="Category Items"
          module="Productions"
          subtitle="List of category items"
        />
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={selectedCategoryItem ? 8 : 12}>
          <Box m="8px 0 0 0" height="80vh">
            <Paper style={{ padding: "1rem", height: "82vh", width: "100%" }}>
              <FormControl fullWidth size="small">
                <InputLabel id="prod-label">Παραγωγή</InputLabel>
                <Select
                  labelId="prod-label"
                  id="prod-id"
                  label="Production"
                  fullWidth
                  size="small"
                  maxWidth={"100%"}
                  value={selectedProduction?.id || ""}
                  onChange={handleProductionChange}
                  style={{ width: "20%", marginBottom: "16px" }}
                >
                  {productions?.map((production) => (
                    <MenuItem key={production.id} value={production.id}>
                      {production.p_descr}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box display="flex" alignItems="center" mb={2}>
                <Typography>Όριο:</Typography>
                <Slider
                  value={threshold}
                  min={0.1}
                  max={1}
                  step={0.05}
                  onChange={handleThresholdChange}
                  valueLabelDisplay="auto"
                  style={{ marginLeft: "16px", width: "200px" }}
                />
                <Typography>{Math.round(threshold * 100)}%</Typography>
              </Box>

              {/* Show loading indicator only after a production is selected */}
              {isLoading && <LinearProgress />}

              {/*If no prod. is selected yet*/}
              {!selectedProduction && (
                <TableContainer sx={{ maxHeight: "70vh", overflow: "auto" }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: "bold", borderRight: "1px solid #ddd" }}>Στοιχεία</TableCell>
                        <TableCell sx={{ fontWeight: "bold", borderRight: "1px solid #ddd" }}>Προϋπ.</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell colSpan={2} align="center">Επιλέξτε μια παραγωγή</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {/*Table*/}
              {selectedProduction && categoryItems && !isLoading && (
                <TableContainer 
                  sx={{
                    height: "66vh",
                    overflow: "auto",
                    "& .MuiTableCell-root": {
                      borderRight: "1px solid #ddd",
                      borderBottom: "none",
                    },
                    "& .MuiTableHead-root .MuiTableCell-root": {
                      borderRight: "1px solid #ddd",
                      backgroundColor: "#f5f5f5",
                    },
                  }}
                >
                  <Table stickyHeader>
                    <TableHead
                      sx={{
                        position: "sticky",
                        top: 0,
                        zIndex: 12,
                        backgroundColor: "#f5f5f5",
                      }}
                    >
                      {/* Level 1 Headers - Top Row */}
                      <TableRow
                        sx={{
                          position: "sticky",
                          top: 0,
                          zIndex: 12,
                        }}
                      >
                        {/* Unamortized header spanning 3 columns (Στοιχείο, Προϋπολογισμός, Μέσος Όρος) */}
                        <TableCell
                          colSpan={3}
                          align="center"
                          sx={{ 
                            fontWeight: "bold",
                            borderRight: "1px solid #ddd",
                            borderBottom: "1px solid #ddd",
                            backgroundColor: "#f5f5f5",
                            padding: "8px 16px",
                            position: "sticky",
                            left: 0,
                            top: 0,
                            zIndex: 13,
                            minWidth: "200px",
                          }}
                        >
                          {columnGroupingModel.find(g => g.groupId === "unamortized")?.headerName}
                        </TableCell>
                        {/* Episode headers in order - with projection values */}
                        {Array.from({ length: selectedProduction.no_of_episodes }, (_, i) => {
                          const episodeNumber = i + 1;
                          const field = `episode-${episodeNumber}`;
                          if (!isColumnVisible(field)) return null;
                          
                          // Calculate projection value
                          const totalOfFullAssigned = rows.reduce(
                            (sum, row) => sum + (parseFloat(row[field]?.maxEpAm) || 0),
                            0
                          );
                          const projectionValue = totalOfFullAssigned * (selectedProduction.no_of_episodes - i);
                          const formattedProjection = formatCurrency(projectionValue);
                          
                          return (
                            <TableCell
                              key={`ep-level1-${episodeNumber}`}
                              colSpan={1}
                              rowSpan={1}
                              align="right"
                              sx={{
                                fontWeight: "bold", 
                                borderRight: "1px solid #ddd",
                                borderBottom: "1px solid #ddd",
                                backgroundColor: "#f5f5f5",
                                padding: "8px 16px",
                              }}
                            >
                              {formattedProjection}
                            </TableCell>
                          );
                        })}
                        {/* Budget Total header */}
                        {isColumnVisible("budgetTotal") && (
                          <TableCell
                            key="budget-total-level1"
                            colSpan={1}
                            rowSpan={1}
                            align="center"
                            sx={{ 
                              fontWeight: "bold", 
                      borderRight: "1px solid #ddd",
                              borderBottom: "1px solid #ddd",
                              backgroundColor: "#f5f5f5",
                              padding: "8px 16px",
                            }}
                          >
                          </TableCell>
                        )}
                        {/* Total Actual Cost header */}
                        {isColumnVisible("totalItemActualCost") && (
                          <TableCell
                            key="total-cost-level1"
                            colSpan={1}
                            rowSpan={1}
                            align="center"
                            sx={{ 
                      fontWeight: "bold",
                              borderRight: "1px solid #ddd",
                              borderBottom: "1px solid #ddd",
                              backgroundColor: "#f5f5f5",
                              padding: "8px 16px",
                            }}
                          >
                          </TableCell>
                        )}
                      </TableRow>
                      {/* Level 2 Headers - Middle Row */}
                      <TableRow
                        sx={{
                          position: "sticky",
                          top: "48px", // Height of Level 1 row
                          zIndex: 11,
                        }}
                      >
                        {/* Σύνολο - Item column (empty) */}
                        <TableCell
                          align="center"
                          sx={{ 
                            fontWeight: "bold",
                            borderRight: "1px solid #ddd",
                            borderBottom: "1px solid #ddd",
                            backgroundColor: "#f5f5f5",
                            padding: "8px 16px",
                            position: "sticky",
                            left: 0,
                            top: "48px",
                            zIndex: 12,
                            minWidth: "200px",
                            maxWidth: "250px",
                            width: "200px",
                          }}
                        >
                          Σύνολο
                        </TableCell>
                        {/* Budget Total */}
                        <TableCell
                          align="right"
                          sx={{ 
                            fontWeight: "bold",
                            borderRight: "1px solid #ddd",
                            borderBottom: "1px solid #ddd",
                            backgroundColor: "#f5f5f5",
                            padding: "8px 16px",
                            position: "sticky",
                            left: "200px",
                            top: "48px",
                            zIndex: 12,
                            minWidth: "100px",
                            maxWidth: "120px",
                            width: "100px",
                          }}
                        >
                          {formatCurrency(
                            categoryItems?.reduce(
                              (sum, item) => sum + (parseFloat(item.pci_amount) || 0),
                              0
                            ) || 0
                          )}
                        </TableCell>
                        {/* Average Total */}
                        <TableCell
                          align="right"
                          sx={{ 
                            fontWeight: "bold",
                            borderRight: "1px solid #ddd",
                            borderBottom: "1px solid #ddd",
                            backgroundColor: "#f5f5f5",
                            padding: "8px 16px",
                            position: "sticky",
                            left: "300px",
                            top: "48px",
                            zIndex: 12,
                            minWidth: "100px",
                            maxWidth: "120px",
                            width: "100px",
                          }}
                        >
                          {formatCurrency(
                            rows.reduce((sum, row) => sum + (parseFloat(row.average) || 0), 0)
                          )}
                        </TableCell>
                        {/* Episode totals in order */}
                        {Array.from({ length: selectedProduction.no_of_episodes }, (_, i) => {
                          const episodeNumber = i + 1;
                          const field = `episode-${episodeNumber}`;
                          if (!isColumnVisible(field)) return null;
                          
                          // Calculate episode sum
                          const episodeSum = rows.reduce(
                            (sum, row) => sum + parseFloat(row[field]?.amount || 0),
                            0
                          );
                          const accumulatedBudget = categoryItems?.reduce(
                            (sum, item) => sum + (parseFloat(item.pci_amount) || 0),
                            0
                          ) || 0;
                          const isExceeded = episodeSum > accumulatedBudget;
                          const episodeColor = isExceeded ? "red" : "#2bc12b";
                          const formattedEpisodeSum = formatCurrency(episodeSum);

                        return (
                            <TableCell
                              key={`ep-level2-${episodeNumber}`}
                              colSpan={1}
                              align="right"
                              sx={{ 
                                fontWeight: "bold", 
                                borderRight: "1px solid #ddd",
                                borderBottom: "1px solid #ddd",
                                backgroundColor: "#f5f5f5",
                                padding: "8px 16px",
                                color: episodeColor + " !important",
                              }}
                            >
                              <span style={{ color: episodeColor }}>
                                {formattedEpisodeSum}
                              </span>
                            </TableCell>
                          );
                        })}
                        {/* Budget Total value */}
                        {isColumnVisible("budgetTotal") && (() => {
                          const budgetTotalGroup = columnGroupingModel.find(
                            (g) => g.groupId === `Ep. ${selectedProduction.no_of_episodes + 1}`
                          );
                          const headerContent = budgetTotalGroup?.headerName || "";
                        return (
                            <TableCell
                              key="budget-total-level2"
                              colSpan={1}
                              align="right"
                              sx={{ 
                                fontWeight: "bold", 
                                borderRight: "1px solid #ddd",
                                borderBottom: "1px solid #ddd",
                                backgroundColor: "#f5f5f5",
                                padding: "8px 16px",
                              }}
                            >
                              {typeof headerContent === "string" ? headerContent : ""}
                            </TableCell>
                          );
                        })()}
                        {/* Total Actual Cost value */}
                        {isColumnVisible("totalItemActualCost") && (() => {
                          const totalCostGroup = columnGroupingModel.find(
                            (g) => g.groupId === `Ep. ${selectedProduction.no_of_episodes + 2}`
                          );
                          const headerContent = totalCostGroup?.headerName || "";
                          return (
                            <TableCell
                              key="total-cost-level2"
                              colSpan={1}
                              align="right"
                              sx={{ 
                                fontWeight: "bold", 
                                borderRight: "1px solid #ddd",
                                borderBottom: "1px solid #ddd",
                                backgroundColor: "#f5f5f5",
                                padding: "8px 16px",
                              }}
                            >
                              {typeof headerContent === "string" ? headerContent : ""}
                            </TableCell>
                          );
                        })()}
                      </TableRow>
                      {/* Level 3 Headers - Bottom Row */}
                      <TableRow
                        sx={{
                          position: "sticky",
                          top: "96px", // Height of Level 1 + Level 2 rows
                          zIndex: 10,
                        }}
                      >
                        {/* Level 3: Column headers - Sticky columns */}
                        <TableCell
                          align="center"
                          sx={{ 
                            fontWeight: "bold", 
                            borderRight: "1px solid #ddd",
                            borderBottom: "1px solid #ddd",
                            backgroundColor: "#f5f5f5",
                            display: isColumnVisible("item") ? "table-cell" : "none",
                            padding: "8px 12px",
                            position: "sticky",
                            left: 0,
                            top: "96px",
                            zIndex: 11,
                            minWidth: "200px",
                            maxWidth: "250px",
                            width: "200px",
                          }}
                        >
                          <TableSortLabel
                            active={sortConfig.field === "item"}
                            direction={sortConfig.field === "item" ? sortConfig.direction : "asc"}
                            onClick={() => handleSort("item")}
                          >
                            Στοιχείο
                          </TableSortLabel>
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{ 
                            fontWeight: "bold", 
                            borderRight: "1px solid #ddd",
                            borderBottom: "1px solid #ddd",
                            backgroundColor: "#f5f5f5",
                            display: isColumnVisible("budget") ? "table-cell" : "none",
                            padding: "8px 12px",
                            position: "sticky",
                            left: "200px",
                            top: "96px",
                            zIndex: 11,
                            minWidth: "100px",
                            maxWidth: "120px",
                            width: "100px",
                          }}
                        >
                          <TableSortLabel
                            active={sortConfig.field === "budget"}
                            direction={sortConfig.field === "budget" ? sortConfig.direction : "asc"}
                            onClick={() => handleSort("budget")}
                          >
                            Προϋπ.
                          </TableSortLabel>
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{ 
                            fontWeight: "bold", 
                            borderRight: "1px solid #ddd",
                            borderBottom: "1px solid #ddd",
                            backgroundColor: "#f5f5f5",
                            display: isColumnVisible("average") ? "table-cell" : "none",
                            padding: "8px 12px",
                            position: "sticky",
                            left: "300px",
                            top: "96px",
                            zIndex: 11,
                            minWidth: "100px",
                            maxWidth: "120px",
                            width: "100px",
                          }}
                        >
                          <TableSortLabel
                            active={sortConfig.field === "average"}
                            direction={sortConfig.field === "average" ? sortConfig.direction : "asc"}
                            onClick={() => handleSort("average")}
                          >
                            Μέσος Όρος
                          </TableSortLabel>
                        </TableCell>
                        {/* Episode columns - Level 3 */}
                        {Array.from({ length: selectedProduction.no_of_episodes }, (_, i) => {
                        const episodeNumber = i + 1;
                        const lockedEpisodes = Array.isArray(selectedProduction?.locked_episodes) 
                          ? selectedProduction.locked_episodes 
                          : [];
                        const isLocked = lockedEpisodes.includes(episodeNumber);
                          const field = `episode-${episodeNumber}`;

                          if (!isColumnVisible(field)) return null;

                          return (
                            <TableCell
                              key={`ep-level3-${episodeNumber}`}
                              align="center"
                              sx={{
                                fontWeight: "bold",
                                borderRight: "1px solid #ddd",
                                borderBottom: "1px solid #ddd",
                                backgroundColor: "#f5f5f5",
                                minWidth: 100,
                                width: 100,
                                padding: "8px 8px",
                              }}
                            >
                              <TableSortLabel
                                active={sortConfig.field === field}
                                direction={sortConfig.field === field ? sortConfig.direction : "asc"}
                                onClick={() => handleSort(field)}
                                sx={{ justifyContent: "center" }}
                              >
                                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
                                  {isLocked && <LockIcon sx={{ fontSize: 16, color: "#ff9800" }} />}
                                  <span>E-{episodeNumber}</span>
                                </Box>
                              </TableSortLabel>
                            </TableCell>
                          );
                        })}
                        {/* Budget Total - Level 3 */}
                        {isColumnVisible("budgetTotal") && (
                          <TableCell
                            align="center"
                            sx={{ 
                              fontWeight: "bold", 
                              borderRight: "1px solid #ddd",
                              borderBottom: "1px solid #ddd",
                              backgroundColor: "#f5f5f5",
                              padding: "8px 16px",
                            }}
                          >
                            <TableSortLabel
                              active={sortConfig.field === "budgetTotal"}
                              direction={sortConfig.field === "budgetTotal" ? sortConfig.direction : "asc"}
                              onClick={() => handleSort("budgetTotal")}
                            >
                              Σύνολο Προϋπ.
                            </TableSortLabel>
                          </TableCell>
                        )}
                        {/* Total Actual Cost - Level 3 */}
                        {isColumnVisible("totalItemActualCost") && (
                          <TableCell
                            align="center"
                            sx={{ 
                              fontWeight: "bold", 
                              borderRight: "1px solid #ddd",
                              borderBottom: "1px solid #ddd",
                              backgroundColor: "#f5f5f5",
                              padding: "8px 16px",
                            }}
                          >
                            <TableSortLabel
                              active={sortConfig.field === "totalItemActualCost"}
                              direction={sortConfig.field === "totalItemActualCost" ? sortConfig.direction : "asc"}
                              onClick={() => handleSort("totalItemActualCost")}
                            >
                              Σύνολο Δαπάνης
                            </TableSortLabel>
                          </TableCell>
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedRows.map((row) => (
                        <TableRow 
                          key={row.id} 
                          hover
                          sx={{
                            "& .MuiTableCell-root": {
                              borderRight: "1px solid #ddd",
                              borderBottom: "none",
                              padding: "8px 16px",
                            },
                            "&:hover .MuiTableCell-root": {
                              backgroundColor: (theme) => theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.04)",
                            },
                            "&:hover .MuiTableCell-root[data-sticky='true']": {
                              backgroundColor: (theme) => theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.04)",
                            },
                          }}
                        >
                          {/* Item column */}
                          <TableCell
                            data-sticky="true"
                            sx={{ 
                              borderRight: "1px solid #ddd",
                              borderBottom: "none",
                              display: isColumnVisible("item") ? "table-cell" : "none",
                              position: "sticky",
                              left: 0,
                              zIndex: 9,
                              backgroundColor: "white",
                              minWidth: "200px",
                              maxWidth: "250px",
                              width: "200px",
                              padding: "8px 12px",
                            }}
                          >
                            {row.item}
                          </TableCell>
                          {/* Budget column */}
                          <TableCell
                            data-sticky="true"
                            align="right"
                            sx={{ 
                              borderRight: "1px solid #ddd",
                              borderBottom: "none",
                              display: isColumnVisible("budget") ? "table-cell" : "none",
                              position: "sticky",
                              left: "200px",
                              zIndex: 9,
                              backgroundColor: "white",
                              minWidth: "100px",
                              maxWidth: "120px",
                              width: "100px",
                              padding: "8px 12px",
                            }}
                          >
                            {formatCurrency(row.budget)}
                          </TableCell>
                          {/* Average column */}
                          <TableCell
                            data-sticky="true"
                            align="right"
                            sx={{ 
                              borderRight: "1px solid #ddd",
                              borderBottom: "none",
                              display: isColumnVisible("average") ? "table-cell" : "none",
                              position: "sticky",
                              left: "300px",
                              zIndex: 9,
                              backgroundColor: "white",
                              minWidth: "100px",
                              maxWidth: "120px",
                              width: "100px",
                              padding: "8px 12px",
                            }}
                          >
                            {formatCurrency(row.average)}
                          </TableCell>
                          {Array.from({ length: selectedProduction.no_of_episodes }, (_, i) => {
                            const episodeNumber = i + 1;
                            const field = `episode-${episodeNumber}`;
                            if (!isColumnVisible(field)) return null;
                            return renderEpisodeCell(row, episodeNumber);
                          })}
                          <TableCell
                            align="right"
                            sx={{ 
                              borderRight: "1px solid #ddd",
                              borderBottom: "none",
                              display: isColumnVisible("budgetTotal") ? "table-cell" : "none",
                            }}
                          >
                            {formatCurrency(row.budgetTotal)}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ 
                              borderRight: "1px solid #ddd",
                              borderBottom: "none",
                              display: isColumnVisible("totalItemActualCost") ? "table-cell" : "none",
                            }}
                          >
                            {formatCurrency(row.totalItemActualCost)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </Box>
        </Grid>
        {selectedItem && selectedColumn > 0 && (
          <Drawer
            anchor="right"
            open={Boolean(selectedItem)}
            onClose={handlePanelClose}
            PaperProps={{
              style: { width: "400px" },
            }}
          >
            <ProductionStatusDetails
              item={selectedItem}
              column={selectedColumn}
              details={itemsDetails}
              itemName={selectedItemName}
              budget={selectedBudget}
              threshold={threshold}
              onClose={handlePanelClose}
            />
          </Drawer>
        )}
      </Grid>
    </Box>
  );
};

export default EpisodeGrid;
